/**
 * A tabela com ficha — onda 212.
 *
 * O pedido (Humberto, 28/09/2026, com os protótipos do artefato «Listas do
 * LabManager», linhas F e G):
 *
 * «Gostaria de lista (Opção A) e ao clicar sobre a linha, abre a ficha a
 *  direita. Ao deselecionar a linha a ficha se fecha mostrando as colunas
 *  que ficaram omitidas da lista.»
 *
 * Uma lista é UMA tabela. O que fica sempre na tabela é o que identifica a
 * linha e o que decide a próxima ação (situação, prazo); o resto é
 * DETALHE: quando a ficha abre, as colunas de detalhe saem da tabela e o
 * que elas diziam passa a morar na ficha — a informação não some, muda de
 * lugar. Clicar de novo na mesma linha, no ✕ ou apertar Esc fecha a ficha
 * e as colunas voltam.
 *
 * O componente não sabe o que é uma OS nem um ensaio: cada tela diz quais
 * colunas tem (o `<th>` e a célula inteiros, para que a ordenação e as
 * marcações de cada tela continuem as mesmas), quais são detalhe, e o que
 * a ficha mostra.
 *
 * Três cuidados:
 *
 *  · clicar num LINK ou botão dentro da linha faz o que o link faz — não
 *    abre a ficha por baixo (a lista de OS tem o número como link, a fila
 *    tem «lançar», «ver laudo», «PDF»);
 *  · a linha é alcançável por teclado: Tab chega nela, Enter ou espaço
 *    abre e fecha, ↑ e ↓ andam — e, com a ficha aberta, a ficha anda
 *    junto;
 *  · se o filtro tirar da lista a linha que estava aberta, a ficha fecha
 *    sozinha: uma ficha de algo que não está mais na tabela é uma ficha
 *    solta.
 *
 * ── No celular, cartões (onda 309, spec 06 §6 item 3, RN-251 c) ──────
 *
 * «Cartão de lista é um alvo só e leva direto ao registro» (D3, Humberto,
 * 08/10/2026). A 412 px a tabela rolava de lado dentro do quadro, e o
 * toque na linha abria uma ficha por cima da tela inteira — o registro
 * ficava a mais um toque, num link de 20 px dentro dela. Abaixo de 640 px,
 * a tela que passa `cartao` E `destino` ganha uma lista de cartões: cada um
 * é UM link (`a.tf-cartao`) para `destino(l)`, com o que `cartao(l)`
 * desenha dentro; sem tabela e sem ficha. `marca(l)`, quando vem (a caixa
 * de marcar das Amostras, no modo «Selecionar»), fica à esquerda do cartão,
 * e o cartão deixa de ser link: vira o rótulo da caixa, e tocar nele marca
 * e desmarca (R47) — quem está selecionando não quer abrir o registro. Quem
 * não passa os dois continua tabela com ficha no celular também (Clientes,
 * Locais, Medições…).
 *
 * O link do cartão NÃO leva `aria-label` (Ruling R19): o nome dele é o que
 * ele mostra — o número, a situação, o cliente, o prazo. Um rótulo por cima
 * trocaria tudo isso por `nome(l)`: o leitor de tela diria só «OS 2612296,
 * link», dois lotes da mesma OS virariam dois links de nome igual para
 * lugares diferentes, e o título que se vê («L2026-0001») nem estaria no
 * nome (WCAG 2.5.3 e 2.4.4). O `aria-label` da linha da tabela continua.
 *
 * A lixeira (246) não vai para o cartão: ela é da exclusão definitiva, que
 * se faz no desktop; um botão dentro do cartão seria o «link de 20 px
 * dentro dele» que a RN-251 c tira.
 */
import {
  useCallback, useEffect, useRef, useState,
  type KeyboardEvent, type MouseEvent, type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Lixeira, useLixeira } from './Lixeira';
import { useEstreito } from './ui';

export interface ColunaComFicha<T> {
  chave: string;
  /** O `<th>` inteiro (uma `ColunaOrdenavel` ou um `<th scope="col">`). */
  cabeca: ReactNode;
  /** A célula inteira (`<th scope="row">` ou `<td>`). */
  celula: (linha: T) => ReactNode;
  /** Sai da tabela quando a ficha abre — e o que dizia vai para a ficha. */
  detalhe?: boolean;
}

export interface FichaDaLinha {
  /** «Ordem de serviço», «Ensaio» — o que é a coisa aberta. */
  rotulo: string;
  titulo: ReactNode;
  /** Se o título é um código (número da OS), vai em fonte mono e maior. */
  tituloCodigo?: boolean;
  sub?: ReactNode;
  corpo: ReactNode;
  /** Até dois links/botões lado a lado; o último é o principal. Um
   *  `.tf-acao-larga` (281) ocupa a linha inteira, acima deles. */
  acoes?: ReactNode;
}

interface Props<T> {
  linhas: T[];
  chave: (linha: T) => string;
  /** Como a linha se chama para quem ouve a tela: «OS 2606101». */
  nome: (linha: T) => string;
  colunas: ColunaComFicha<T>[];
  ficha: (linha: T) => FichaDaLinha;
  /** O `aria-label` da tabela. */
  rotulo: string;
  classeDaLinha?: (linha: T) => string | undefined;
  /**
   * A lixeira no fim de cada linha (onda 246) — só para quem tem a
   * exclusão definitiva; para os outros, nem a coluna existe.
   */
  lixeira?: {
    tipo: string;
    id: (linha: T) => string;
    rotulo: (linha: T) => string;
    aoApagar(): void;
  };
  /** No celular: o que o cartão da linha mostra (309). Precisa de `destino`. */
  cartao?: (l: T) => ReactNode;
  /** No celular: para onde o toque no cartão leva — o registro (D3). */
  destino?: (l: T) => string;
  /** No celular: a caixa de marcar ao lado do cartão; com ela, o cartão marca em vez de levar (R47). */
  marca?: (l: T) => ReactNode;
  /**
   * A linha que abre com a tabela (onda 313): quem chega de outra tela com
   * a ficha pedida no endereço — o «Fotos (n)» da vertical CPTu volta à
   * ficha dela. Só na montagem; depois, quem abre e fecha é a pessoa.
   */
  inicial?: string | null;
}

/** O que, dentro da linha, tem vida própria e não abre a ficha. */
const INTERATIVO = 'a, button, input, select, textarea, label, summary, [role="button"]';

export function TabelaComFicha<T>({
  linhas, chave, nome, colunas, ficha, rotulo, classeDaLinha, lixeira, inicial,
  cartao, destino, marca,
}: Props<T>) {
  const estreito = useEstreito(640);
  const comLixeira = useLixeira() && !!lixeira;
  const [aberta, setAberta] = useState<string | null>(inicial ?? null);
  const corpo = useRef<HTMLTableSectionElement>(null);
  const idFicha = useRef(`tf-${Math.random().toString(36).slice(2, 9)}`).current;

  const linhaAberta = aberta === null
    ? null : linhas.find((l) => chave(l) === aberta) ?? null;

  /* O filtro tirou a linha aberta da lista: a ficha fecha. */
  useEffect(() => {
    if (aberta !== null && !linhaAberta) setAberta(null);
  }, [aberta, linhaAberta]);

  const focarLinha = useCallback((k: string) => {
    const tr = corpo.current?.querySelector<HTMLTableRowElement>(
      `tr[data-chave="${CSS.escape(k)}"]`);
    tr?.focus();
  }, []);

  const fechar = useCallback(() => {
    const k = aberta;
    setAberta(null);
    if (k) requestAnimationFrame(() => focarLinha(k));
  }, [aberta, focarLinha]);

  const alternar = (k: string) => setAberta((a) => (a === k ? null : k));

  const aoClicar = (k: string) => (e: MouseEvent<HTMLTableRowElement>) => {
    if ((e.target as HTMLElement).closest(INTERATIVO)) return;
    alternar(k);
  };

  const aoTeclar = (k: string, i: number) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      alternar(k);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const j = i + (e.key === 'ArrowDown' ? 1 : -1);
      if (j < 0 || j >= linhas.length) return;
      const outra = chave(linhas[j]);
      focarLinha(outra);
      if (aberta !== null) setAberta(outra);
    } else if (e.key === 'Escape' && aberta !== null) {
      e.preventDefault();
      fechar();
    }
  };

  /* O celular, com cartão e destino: cartões que levam ao registro. */
  if (estreito && cartao && destino) {
    return (
      <ul className="tf-cartoes" aria-label={rotulo}>
        {linhas.map((l) => {
          const k = chave(l);
          const caixa = marca?.(l);
          return (
            <li key={k} data-chave={k}
              className={['tf-cartao-item', classeDaLinha?.(l)].filter(Boolean).join(' ')}>
              {caixa ? (
                /* R47: no «Selecionar» o cartão inteiro é o rótulo da caixa —
                   tocar nele marca e desmarca, e não sai da lista. */
                <label className="tf-cartao-marcar">
                  {caixa}
                  <span className="tf-cartao">{cartao(l)}</span>
                </label>
              ) : (
                <Link className="tf-cartao" to={destino(l)}>
                  {cartao(l)}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  const visiveis = linhaAberta ? colunas.filter((c) => !c.detalhe) : colunas;
  const f = linhaAberta ? ficha(linhaAberta) : null;

  return (
    <div className={`tf${linhaAberta ? ' tf-aberta' : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && linhaAberta && !e.defaultPrevented) {
          e.preventDefault();
          fechar();
        }
      }}>
      <div className="rola tf-rola">
        <table className="tf-tabela" aria-label={rotulo}>
          <thead>
            <tr>
              {visiveis.map((c) => <Fragmento key={c.chave}>{c.cabeca}</Fragmento>)}
              {comLixeira && (
                <th scope="col" className="col-lixeira" aria-label="Excluir" />
              )}
            </tr>
          </thead>
          <tbody ref={corpo}>
            {linhas.map((l, i) => {
              const k = chave(l);
              const esta = k === aberta;
              const classe = [classeDaLinha?.(l), 'tf-linha', esta ? 'tf-sel' : '']
                .filter(Boolean).join(' ');
              return (
                <tr key={k} data-chave={k} className={classe}
                  tabIndex={0}
                  aria-selected={esta}
                  aria-controls={esta ? idFicha : undefined}
                  aria-label={`${nome(l)} — ${esta ? 'fechar' : 'abrir'} a ficha`}
                  onClick={aoClicar(k)}
                  onKeyDown={aoTeclar(k, i)}>
                  {visiveis.map((c) => <Fragmento key={c.chave}>{c.celula(l)}</Fragmento>)}
                  {comLixeira && lixeira && (
                    <td className="col-lixeira">
                      <Lixeira tipo={lixeira.tipo} id={lixeira.id(l)}
                        rotulo={lixeira.rotulo(l)} aoApagar={lixeira.aoApagar} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {linhaAberta && f && (
        <aside id={idFicha} className="tf-ficha"
          aria-label={`Ficha: ${nome(linhaAberta)}`}>
          <div className="tf-ficha-cabeca">
            <div className="tf-ficha-titulos">
              <span className="tf-ficha-rotulo">{f.rotulo}</span>
              <div className={`tf-ficha-titulo${f.tituloCodigo ? ' mono' : ''}`}>
                {f.titulo}
              </div>
              {f.sub && <div className="tf-ficha-sub">{f.sub}</div>}
            </div>
            <button type="button" className="tf-fechar"
              aria-label="Fechar a ficha" title="Fechar (Esc)" onClick={fechar} />
          </div>
          <div className="tf-ficha-corpo">{f.corpo}</div>
          {f.acoes && <div className="tf-ficha-acoes">{f.acoes}</div>}
        </aside>
      )}
    </div>
  );
}

/** `<>…</>` com chave, sem importar `Fragment` em cada tela. */
function Fragmento({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/**
 * Os pares «o quê · valor» da ficha. Um valor vazio não vira linha: uma
 * ficha cheia de «—» ensina a não ler a ficha.
 */
export function CamposDaFicha({ itens }: { itens: [string, ReactNode][] }) {
  const cheios = itens.filter(([, v]) => v !== null && v !== undefined
    && v !== false && v !== '');
  return (
    <dl className="tf-campos">
      {cheios.map(([r, v]) => (
        <div key={r} className="tf-campo">
          <dt>{r}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
